/**
 * @voltxampere/presets — Internal Circuit / Net / Component builders
 *
 * Hand-writing every preset's CircuitV1 with all required UUIDs,
 * timestamps, default simulation settings, and terminal arrays would
 * blow up to 200+ lines per preset. The builders here flatten the
 * boilerplate to ~30-50 LoC per preset while still producing
 * SPECS §1-compliant CircuitV1 documents.
 *
 * Builders are pure data — they do NOT call `validateCircuitInvariants`
 * or `planCircuit`. The registry-level test suite parses every preset
 * through `CircuitSchema` so any builder bug surfaces at the boundary.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { pinLocalForTerminal, pinWorldCoord } from "./_layout-helpers.js";
/**
 * Frozen authoring timestamp for every preset (2026-04-27 UTC).
 * Snapshotting a constant value keeps the schema validation tests
 * deterministic — generating `Date.now()` at module load would put
 * a moving timestamp into every CircuitV1 and break snapshot tests
 * downstream.
 */
const FROZEN_TIMESTAMP = 1_745_712_000_000;
/** Schema version pinned by Sprint 17. Mirrors `canvas-demo` D8. */
const SCHEMA_VERSION = "0.4.0";
/** Net builder — auto-prefixes id with `net_`. */
export function makeNet(spec) {
    return {
        id: spec.id ?? `net_${spec.name}`,
        name: spec.name,
        isGround: spec.isGround ?? false,
    };
}
/** Component builder — pin id → net id mapping with optional layout. */
export function makeComponent(spec) {
    const position = spec.position ?? { x: 0, y: 0 };
    const rotation = spec.rotation ?? 0;
    const overrides = spec.terminalPositions ?? {};
    const terminals = Object.entries(spec.terminals).map(([terminalId, netId]) => {
        const override = overrides[terminalId];
        if (override !== undefined) {
            return { terminalId, netId, position: override };
        }
        const local = pinLocalForTerminal(spec.type, terminalId);
        const pinPos = local !== undefined ? pinWorldCoord(position, rotation, local) : { x: 0, y: 0 };
        return { terminalId, netId, position: pinPos };
    });
    const instance = {
        id: spec.id,
        type: spec.type,
        position,
        rotation,
        flipped: false,
        params: { ...(spec.params ?? {}) },
        terminals,
    };
    if (spec.label !== undefined) {
        return { ...instance, label: spec.label };
    }
    return instance;
}
/**
 * Reasonable per-preset id generator. The CircuitV1 schema's UUID
 * constraint is checked by zod at load time, so we hand-author a
 * stable, version-4-shaped UUID per preset slot.
 *
 * Form: `vxa-pre-XXXX-XXXX-XXXX-XXXXXXXXXXXX` where each `X` is hex
 * derived from the slot index — the first 16 slots are reserved for
 * the Sprint 17 set, slots 17+ become available for Sprint 22+
 * additions without renumbering the existing presets.
 *
 * Wait — UUID v4 RFC requires the third group to start with `4` and
 * the fourth with `8/9/a/b`. Hand-rolling a sequence within those
 * constraints is ugly; instead we map slot index N (0..255) into a
 * fixed valid-v4 template:
 *   `00000000-0000-4000-8000-NNNN00000000`
 * with the slot in the trailing block. Test fixture-style — no
 * cryptographic value, only schema validity.
 */
export function presetUuid(slot) {
    if (slot < 0 || slot > 0xffff) {
        throw new RangeError(`presetUuid: slot ${String(slot)} out of range [0, 65535]`);
    }
    const slotHex = slot.toString(16).padStart(4, "0");
    return `00000000-0000-4000-8000-${slotHex}00000000`;
}
/** Build the simulation-settings block. Always populated. */
function defaultSimulationSettings(spec) {
    const dc = {
        temperature: 27,
        gmin: 1e-12,
        reltol: 1e-3,
        abstol: 1e-12,
        maxIterations: 100,
        enableDeltaVDamping: true,
        toleranceStrategy: "strict",
        enableAnderson: false,
        enableArmijo: true,
        ...(spec.initialConditions ? { initialConditions: spec.initialConditions } : {}),
    };
    return {
        defaultAnalysis: spec.defaultAnalysis ?? "dc",
        dc,
        transient: {
            tstart: spec.transient?.tstart ?? 0,
            tstop: spec.transient?.tstop ?? 1e-3,
            method: spec.transient?.method ?? "trap",
            reltol: 1e-3,
            abstol: 1e-12,
        },
        ac: {
            start: 1,
            stop: 1e9,
            points: 100,
            sweep: "dec",
        },
    };
}
export function makeCircuit(spec) {
    return {
        schemaVersion: SCHEMA_VERSION,
        id: presetUuid(spec.slot),
        name: spec.name,
        description: spec.description,
        createdAt: FROZEN_TIMESTAMP,
        updatedAt: FROZEN_TIMESTAMP,
        nodes: [],
        nets: [...spec.nets],
        components: [...spec.components],
        wires: spec.wires !== undefined ? [...spec.wires] : [],
        groups: [],
        annotations: [],
        assertions: [],
        simulationSettings: defaultSimulationSettings(spec),
        metadata: { source: "@voltxampere/presets" },
    };
}
//# sourceMappingURL=_builders.js.map